import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Card,
    CardContent,
    Container,
    CssBaseline,
    ThemeProvider,
    createTheme,
    CircularProgress,
    InputAdornment,
    IconButton,
    Grid,
    Paper,
    Divider,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    LockReset as LockResetIcon,
    ArrowBack,
    CheckCircle,
    Security,
    Email,
    VpnKey,
    MarkEmailRead,
    Send,
} from '@mui/icons-material';
import swal from 'sweetalert';
import { getProviderById, enviarCodigoVerificacion, validarCodigoVerificacion, cambiarContrasena, registerUser, getIDWorkmanager, actualizarCorreoUsuario, checkUserExists } from '../Componentes/FunctionLogin';
import { useTranslation } from 'react-i18next';

import logopng from '../recursos/logocompleto.png';

const loginTheme = createTheme({
    palette: {
        primary: {
            main: '#BD1F34',
            dark: '#8a1525',
            light: '#e04f5f',
        },
        text: {
            primary: '#2D3436',
            secondary: '#636E72',
        },
        background: {
            default: '#F7F9FC',
            paper: '#FFFFFF',
        },
    },
    typography: {
        fontFamily: '"Outfit", "Roboto", "Helvetica", "Arial", sans-serif',
        h4: { fontWeight: 700 },
        button: { fontWeight: 600, textTransform: 'none', fontSize: '1rem' },
    },
    shape: { borderRadius: 12 },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '50px',
                    boxShadow: '0 4px 14px 0 rgba(189, 31, 52, 0.39)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        boxShadow: '0 6px 20px rgba(189, 31, 52, 0.23)',
                        transform: 'translateY(-2px)',
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F7F9FC',
                        borderRadius: '12px',
                        '& fieldset': { borderColor: 'transparent' },
                        '&:hover fieldset': { borderColor: '#e0e0e0' },
                        '&.Mui-focused fieldset': { borderColor: '#BD1F34', backgroundColor: 'transparent' },
                    },
                },
            },
        },
    },
});

const OTP_SECONDS = 15 * 60; // 15 minutos en segundos

const maskEmail = (email) => {
    if (!email) return '***@***.***';
    const [user, domain] = email.split('@');
    if (!domain) return email;
    // Mostrar solo los últimos 4 caracteres antes del @
    if (user.length <= 4) return '***' + user + '@' + domain;
    return '***' + user.slice(-4) + '@' + domain;
};

export default function Contrasena() {
    const navigate = useNavigate();
    const [t] = useTranslation("global");

    // step 1 = ingresar usuario+email y enviar código
    // step 2 = validar código de 4 dígitos
    // step 3 = crear nueva contraseña
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    // Step 1
    const [usuarioValue, setUsuarioValue] = useState('');
    const [emailValue, setEmailValue] = useState('');
    const [verifiedUser, setVerifiedUser] = useState(null);

    // Step 2
    const [otpValue, setOtpValue] = useState('');
    const [timeLeft, setTimeLeft] = useState(0);      // seconds remaining
    const [codeExpired, setCodeExpired] = useState(false);
    const timerRef = useRef(null);

    // Step 3
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Timer logic
    const startTimer = () => {
        clearInterval(timerRef.current);
        setTimeLeft(OTP_SECONDS);
        setCodeExpired(false);
        timerRef.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    setCodeExpired(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    useEffect(() => () => clearInterval(timerRef.current), []);

    const formatTime = (secs) => {
        const m = Math.floor(secs / 60).toString().padStart(2, '0');
        const s = (secs % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    // -------------------------------------------------------
    // PASO 1A: Validar usuario en SAP y enviar código al correo
    // -------------------------------------------------------
    const handleSendCode = async () => {
        if (!usuarioValue.trim()) {
            swal(t("contrasena.swal_atencion"), t("activacion_cuenta.swal_ingrese_datos"), "warning");
            return;
        }

        setLoading(true);
        try {
            // Verificar que el usuario existe en SAP
            const sapResult = await getProviderById(usuarioValue.trim());

            if (!sapResult || sapResult.length === 0) {
                swal(t("activacion_cuenta.swal_usuario_no_encontrado"), t("activacion_cuenta.swal_usuario_no_encontrado_desc"), "error");
                return;
            }

            const userData = sapResult[0];
            setVerifiedUser(userData);

            // OBTENCIÓN DEL CORREO PARA EL CÓDIGO
            let foundEmail = "";

            // PRIORIDAD 1: Correo que viene directamente de SAP (getProviderById)
            if (userData.Email && userData.Email.includes("@")) {
                foundEmail = userData.Email.trim();
                console.log("✅ Email obtenido de SAP:", foundEmail);
            }

            // BÚSQUEDA EN WorkManager - Solo si SAP no devolvió el correo
            if (!foundEmail) {
                console.log("⚠️ SAP no devolvió correo. Buscando en WorkManager...");
                const identifiers = [userData.Proveedor, userData.Nit, usuarioValue.trim()];
                for (const id of identifiers) {
                    if (!id) continue;
                    const wmResultItem = await getIDWorkmanager(id);
                    if (wmResultItem && Array.isArray(wmResultItem)) {
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        const emailField = wmResultItem.find(f =>
                            f.Value && typeof f.Value === 'string' && emailRegex.test(f.Value.trim())
                        );
                        if (emailField) {
                            foundEmail = emailField.Value.trim();
                            console.log(`✅ Email encontrado en WM: ${foundEmail}`);
                            break;
                        }
                    }
                }
            }

            // Fallback final: email de sesión activa
            if (!foundEmail && localStorage.getItem("Email")) {
                foundEmail = localStorage.getItem("Email");
            }

            if (!foundEmail) {
                swal(t("contrasena.swal_error"), "No pudimos localizar automáticamente su correo electrónico. Verifique su NIT o contacte soporte técnico.", "error");
                return;
            }

            setEmailValue(foundEmail);

            // PASO: Enviar código
            let sendResult = await enviarCodigoVerificacion(foundEmail);

            if (!sendResult.success) {
                swal(t("activacion_cuenta.swal_error_enviar_codigo"), sendResult.message || t("activacion_cuenta.swal_error_enviar_codigo_desc"), "error");
                return;
            }

            startTimer();
            setOtpValue('');
            swal(t("activacion_cuenta.swal_codigo_enviado"), t("activacion_cuenta.swal_codigo_enviado_desc", { email: maskEmail(foundEmail) }), "success");
            setStep(2);

        } catch (error) {
            console.error(error);
            swal(t("contrasena.swal_error"), t("activacion_cuenta.swal_error_inesperado"), "error");
        } finally {
            setLoading(false);
        }
    };

    // -------------------------------------------------------
    // PASO 2: Validar el código OTP de 6 dígitos
    // -------------------------------------------------------
    const handleVerifyOtp = async () => {
        if (!otpValue || otpValue.length !== 6) {
            swal(t("contrasena.swal_atencion"), t("activacion_cuenta.swal_ingrese_codigo"), "warning");
            return;
        }
        setLoading(true);
        try {
            const result = await validarCodigoVerificacion(emailValue.trim(), otpValue);

            if (!result.success) {
                swal(t("activacion_cuenta.swal_codigo_incorrecto"), result.message || t("activacion_cuenta.swal_codigo_incorrecto_desc"), "error");
                return;
            }

            swal(t("activacion_cuenta.swal_codigo_correcto"), t("activacion_cuenta.swal_codigo_correcto_desc"), "success");
            setStep(3);
        } catch (error) {
            console.error(error);
            swal(t("contrasena.swal_error"), t("activacion_cuenta.swal_error_validar_codigo"), "error");
        } finally {
            setLoading(false);
        }
    };

    // Reenviar código
    const handleResendCode = async () => {
        if (loading) return;
        setLoading(true);
        try {
            const sendResult = await enviarCodigoVerificacion(emailValue.trim());
            if (sendResult.success) {
                swal(t("activacion_cuenta.swal_codigo_reenviado"), t("activacion_cuenta.swal_codigo_reenviado_desc", { email: maskEmail(emailValue.trim()) }), "success");
                setOtpValue('');
            } else {
                swal(t("contrasena.swal_error"), sendResult.message, "error");
            }
        } catch {
            swal(t("contrasena.swal_error"), t("activacion_cuenta.swal_error_reenviar"), "error");
        } finally {
            setLoading(false);
        }
    };

    // -------------------------------------------------------
    // PASO 3: Cambiar contraseña
    // -------------------------------------------------------
    const handleUpdatePassword = async () => {
        if (!newPassword || !confirmPassword) {
            swal(t("contrasena.swal_atencion"), t("activacion_cuenta.swal_ingrese_contrasenas"), "warning");
            return;
        }
        if (newPassword !== confirmPassword) {
            swal(t("contrasena.swal_error"), t("activacion_cuenta.swal_contrasenas_no_coinciden"), "error");
            return;
        }
        if (newPassword.length < 4) {
            swal(t("contrasena.swal_atencion"), t("activacion_cuenta.swal_min_caracteres"), "warning");
            return;
        }
        setLoading(true);
        try {
            // 1. Registrar usuario en BigQuery primero (igual que hacía Login.js)
            const realUsuario = verifiedUser.Proveedor;

            // Usamos la API de registerUser (signup)
            const registerResult = await registerUser(realUsuario, newPassword, emailValue.trim(), 'usuario');

            // Excepcion: Firebase/Google a veces retorna "Failed to convert user", pero sí crea la cuenta
            // También ignoramos si el usuario ya existe, ya que entonces solo necesita actualizar en SAP
            const userAlreadyExists = registerResult.error &&
                (registerResult.error.toLowerCase().includes("el usuario ya existe") ||
                    registerResult.error.toLowerCase().includes("already exists"));

            if (!registerResult.success && !(registerResult.error && registerResult.error.includes("Failed to convert user")) && !userAlreadyExists) {
                swal(t("activacion_cuenta.swal_error_registro"), registerResult.error || t("activacion_cuenta.swal_error_registro_desc"), "error");
                return;
            }

            // 2. Si BigQuery fue exitoso o arrojó la excepción conocida, actualizamos SAP
            const result = await cambiarContrasena(realUsuario, newPassword);

            if (!result.success) {
                swal(t("contrasena.swal_error"), result.message || t("activacion_cuenta.swal_error_actualizar_sap"), "error");
                return;
            }

            swal(t("activacion_cuenta.swal_cuenta_activada"), t("activacion_cuenta.swal_cuenta_activada_desc"), "success")
                .then(() => navigate('/proveedores/login'));
        } catch (error) {
            console.error(error);
            swal(t("contrasena.swal_error"), t("activacion_cuenta.swal_error_activar"), "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <ThemeProvider theme={loginTheme}>
            <CssBaseline />
            <style>
                {`
                    .swal-text {
                        text-align: center;
                    }
                `}
            </style>
            <Grid container component="main" sx={{ minHeight: '100vh', bgcolor: '#F0F2F5', justifyContent: 'center', alignItems: 'center' }}>
                <Container maxWidth="xs" sx={{ px: { xs: 2, sm: 3 } }}>
                    <Card sx={{ borderRadius: 4, boxShadow: '0 8px 40px rgba(0,0,0,0.12)', overflow: 'visible', mt: { xs: 6, sm: 4 }, mb: 4 }}>

                        {/* Icon header */}
                        <Box sx={{ position: 'relative', textAlign: 'center', mt: { xs: -5, sm: -4 }, mb: 2 }}>
                            <Paper elevation={4} sx={{
                                width: { xs: 65, sm: 80 }, height: { xs: 65, sm: 80 }, borderRadius: '50%',
                                display: 'flex', justifyContent: 'center', alignItems: 'center',
                                margin: '0 auto',
                                background: 'linear-gradient(135deg, #BD1F34 0%, #8a1525 100%)',
                                color: 'white'
                            }}>
                                {step === 1 && <LockResetIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />}
                                {step === 2 && <MarkEmailRead sx={{ fontSize: { xs: 32, sm: 40 } }} />}
                                {step === 3 && <CheckCircle sx={{ fontSize: { xs: 32, sm: 40 } }} />}
                            </Paper>
                        </Box>

                        <CardContent sx={{ px: { xs: 2, sm: 4 }, pb: { xs: 3, sm: 4 } }}>

                            {/* Logo + title */}
                            <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
                                <img src={logopng} alt="Talsa" style={{ height: 40, marginBottom: 16, maxHeight: '8vh' }} />
                                <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
                                    {t("activacion_cuenta.titulo")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {step === 1 && t("activacion_cuenta.desc_paso1")}
                                    {step === 2 && t("activacion_cuenta.desc_paso2", { email: maskEmail(emailValue) })}
                                    {step === 3 && t("activacion_cuenta.desc_paso3")}
                                </Typography>
                            </Box>

                            <Divider sx={{ mb: 3 }} />

                            {/* ---- PASO 1: Usuario + Email + botón Enviar Código ---- */}
                            {step === 1 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        label={t("activacion_cuenta.usuario_nit")}
                                        variant="outlined"
                                        fullWidth
                                        value={usuarioValue}
                                        onChange={(e) => setUsuarioValue(e.target.value)}
                                        placeholder={t("activacion_cuenta.placeholder_usuario_nit")}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Security sx={{ color: 'text.secondary' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                                        {t("activacion_cuenta.helper_correo_WM") || "El código se enviará al correo registrado en el sistema."}
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        onClick={handleSendCode}
                                        disabled={loading || !usuarioValue.trim()}
                                        sx={{ mt: 1 }}
                                        endIcon={!loading && <Send />}
                                    >
                                        {loading ? <CircularProgress size={24} color="inherit" /> : t("activacion_cuenta.btn_enviar_codigo")}
                                    </Button>
                                </Box>
                            )}

                            {/* ---- PASO 2: Ingresar código OTP ---- */}
                            {step === 2 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                                    {/* Info del correo */}
                                    <Box sx={{ bgcolor: 'rgba(189,31,52,0.05)', borderRadius: 2, p: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t("activacion_cuenta.codigo_enviado_a")} <strong>{maskEmail(emailValue)}</strong>
                                        </Typography>
                                    </Box>

                                    {/* Countdown timer */}
                                    {!codeExpired ? (
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="body2" sx={{
                                                color: timeLeft < 60 ? '#BD1F34' : '#2e7d32',
                                                fontWeight: 700,
                                                fontSize: '1.1rem'
                                            }}>
                                                {t("activacion_cuenta.codigo_expira_en")} {formatTime(timeLeft)}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ bgcolor: 'rgba(189,31,52,0.08)', borderRadius: 2, p: 2, textAlign: 'center', border: '1px solid #BD1F34' }}>
                                            <Typography variant="body2" sx={{ color: '#BD1F34', fontWeight: 700, mb: 1 }}>
                                                {t("activacion_cuenta.codigo_expirado")}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={handleResendCode}
                                                disabled={loading}
                                                sx={{ borderColor: '#BD1F34', color: '#BD1F34', boxShadow: 'none', '&:hover': { boxShadow: 'none', transform: 'none' } }}
                                            >
                                                {loading ? <CircularProgress size={18} color="inherit" /> : t("activacion_cuenta.solicitar_nuevo")}
                                            </Button>
                                        </Box>
                                    )}

                                    {/* Campo OTP de 6 dígitos */}
                                    <TextField
                                        label={t("activacion_cuenta.codigo_verificacion")}
                                        variant="outlined"
                                        fullWidth
                                        value={otpValue}
                                        disabled={codeExpired}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').substring(0, 6);
                                            setOtpValue(val);
                                        }}
                                        onKeyDown={(e) => e.key === 'Enter' && otpValue.length === 6 && !codeExpired && handleVerifyOtp()}
                                        placeholder="_ _ _ _ _ _"
                                        inputProps={{
                                            maxLength: 6,
                                            style: {
                                                fontSize: '1.8rem',
                                                letterSpacing: '12px',
                                                textAlign: 'center',
                                                fontWeight: 'bold',
                                                paddingLeft: '16px',
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <VpnKey sx={{ color: codeExpired ? '#ccc' : '#BD1F34' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        onClick={handleVerifyOtp}
                                        disabled={loading || otpValue.length !== 6 || codeExpired}
                                        sx={{ mt: 1 }}
                                        endIcon={!loading && <CheckCircle />}
                                    >
                                        {loading ? <CircularProgress size={24} color="inherit" /> : t("activacion_cuenta.btn_verificar")}
                                    </Button>

                                    {!codeExpired && (
                                        <Button
                                            variant="text"
                                            fullWidth
                                            size="small"
                                            disabled={loading}
                                            onClick={handleResendCode}
                                            sx={{
                                                color: 'text.secondary',
                                                boxShadow: 'none',
                                                '&:hover': { boxShadow: 'none', transform: 'none', textDecoration: 'underline' }
                                            }}
                                        >
                                            {t("activacion_cuenta.no_llego_codigo")}
                                        </Button>
                                    )}
                                </Box>
                            )}

                            {/* ---- PASO 3: Nueva contraseña ---- */}
                            {step === 3 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ bgcolor: 'rgba(46,125,50,0.06)', borderRadius: 2, p: 2, textAlign: 'center', mb: 1 }}>
                                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                                            {t("activacion_cuenta.usuario_lbl")} {verifiedUser?.Proveedor}
                                        </Typography>
                                    </Box>

                                    <TextField
                                        label={t("activacion_cuenta.nueva_contrasena")}
                                        type={showPassword ? 'text' : 'password'}
                                        variant="outlined"
                                        fullWidth
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <TextField
                                        label={t("activacion_cuenta.confirmar_contrasena")}
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        variant="outlined"
                                        fullWidth
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleUpdatePassword()}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        onClick={handleUpdatePassword}
                                        disabled={loading || !newPassword || !confirmPassword}
                                        sx={{
                                            mt: 2,
                                            bgcolor: '#2e7d32',
                                            '&:hover': { bgcolor: '#1b5e20' },
                                            boxShadow: '0 4px 14px 0 rgba(46,125,50,0.4)'
                                        }}
                                        endIcon={!loading && <CheckCircle />}
                                    >
                                        {loading ? <CircularProgress size={24} color="inherit" /> : t("activacion_cuenta.btn_activar")}
                                    </Button>
                                </Box>
                            )}

                            {/* Back button */}
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                                <Button
                                    startIcon={<ArrowBack />}
                                    onClick={() => step > 1 ? setStep(step - 1) : navigate('/proveedores/login')}
                                    sx={{
                                        color: 'text.secondary',
                                        boxShadow: 'none',
                                        '&:hover': { boxShadow: 'none', transform: 'none' }
                                    }}
                                >
                                    {step > 1 ? t("activacion_cuenta.volver_paso_anterior") : t("activacion_cuenta.volver_inicio_sesion")}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Container>
            </Grid>
        </ThemeProvider>
    );
}
